/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
    cn
} from 'shared/lib/utils';
import {
    CheckCircle2, AlertCircle, X, ChevronRight, Pencil, AtSign, FunctionSquare,
    Plus, Minus, X as Times, Divide, Equal, ChevronLeft, ChevronUp, ChevronDown,
    Hash, ToggleLeft, ToggleRight, CircleSlash, Variable, GitFork, Workflow,
    Repeat, Search, Code2, Save
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    outputPathsForStep, STEP_META_PATHS
} from './oelTokens';
import {
    OEL_FUNCTIONS
} from './oelTokens';
import {
    extractRefs, validate, insertAt, replaceRange
} from './oelParse';
import './OelEditor.css';

/**
 * OelEditor — unified expression editor used everywhere an OEL string is
 * authored. Single primitive, same UX in workforce + opcode + every step.
 *
 * UX (unified Simple/Advanced 2026-07-22 — supersedes the 2026-04-27
 * inline-is-read-only rule, which had split into two divergent editors):
 *   • At rest the field is a preview button: refs render as colored chips,
 *     literal text shows verbatim, with a live validity icon and a visible
 *     first-error line.
 *   • Clicking the preview body switches to a raw inline input for direct
 *     typing (blur returns to the chip preview).
 *   • The Edit CTA / Builder button opens the LARGE dialog. Inside: a
 *     textarea with @-mention autocomplete + tabs for **References** +
 *     **Functions** + a permanently visible **Operators** + **Constants**
 *     strip with labeled, icon-clad cards. No popovers (they'd close
 *     inside Dialog).
 *   • Both editor modes (Simple and Advanced) get the identical control;
 *     the legacy `inline` prop is accepted for compatibility but no longer
 *     switches the UI.
 */

const OPERATOR_BUTTONS = [
    {
        token: '+',
        label: 'Add',
        icon: Plus,
        tone: 'math',
        spaced: true
    },
    {
        token: '-',
        label: 'Subtract',
        icon: Minus,
        tone: 'math',
        spaced: true
    },
    {
        token: '*',
        label: 'Multiply',
        icon: Times,
        tone: 'math',
        spaced: true
    },
    {
        token: '/',
        label: 'Divide',
        icon: Divide,
        tone: 'math',
        spaced: true
    },
    {
        token: '==',
        label: 'Equals',
        icon: Equal,
        tone: 'compare',
        spaced: true
    },
    {
        token: '!=',
        label: 'Not equals',
        icon: CircleSlash,
        tone: 'compare',
        spaced: true,
        glyph: '≠'
    },
    {
        token: '<',
        label: 'Less than',
        icon: ChevronLeft,
        tone: 'compare',
        spaced: true
    },
    {
        token: '>',
        label: 'Greater than',
        icon: ChevronRight,
        tone: 'compare',
        spaced: true
    },
    {
        token: '<=',
        label: 'Less or eq',
        icon: ChevronDown,
        tone: 'compare',
        spaced: true,
        glyph: '≤'
    },
    {
        token: '>=',
        label: 'Greater or eq',
        icon: ChevronUp,
        tone: 'compare',
        spaced: true,
        glyph: '≥'
    },
    {
        token: 'and',
        label: 'AND',
        icon: Variable,
        tone: 'logic',
        spaced: true,
        glyph: '&'
    },
    {
        token: 'or',
        label: 'OR',
        icon: Variable,
        tone: 'logic',
        spaced: true,
        glyph: '|'
    },
    {
        token: 'not',
        label: 'NOT',
        icon: CircleSlash,
        tone: 'logic',
        spaced: true,
        glyph: '!'
    }
];

const CONSTANT_BUTTONS = [
    {
        token: 'true',
        label: 'TRUE',
        icon: ToggleRight,
        tone: 'pos'
    },
    {
        token: 'false',
        label: 'FALSE',
        icon: ToggleLeft,
        tone: 'neg'
    },
    {
        token: 'null',
        label: 'NULL',
        icon: CircleSlash,
        tone: 'neutral'
    }
];

export default function OelEditor({
    value = '',
    onChange,
    ctx,
    placeholder = '@input.field',
    multiline = false, // accepted for back-compat; ignored — dialog handles all editing
    compact = false,
    inline = false, // accepted for back-compat; the unified control ignores it
    ariaLabel,
    expectedType
}) {
    const [open, setOpen] = useState(false);
    const [typing, setTyping] = useState(false);
    const inputRef = useRef(null);
    const validation = useMemo(() => validate(value, ctx), [value, ctx]);

    useEffect(() => {
        if (typing) inputRef.current?.focus();
    }, [typing]);

    return (
        <>
            {typing ? (
                <div className="flex gap-1">
                    <Input
                        ref={inputRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={() => setTyping(false)}
                        placeholder={placeholder}
                        aria-label={ariaLabel || 'OEL expression'}
                        className={cn(
                            'min-w-0 flex-1 font-mono text-xs',
                            compact && 'h-7'
                        )}
                    />
                    <Button
                        type="button"
                        variant="outline"
                        size={compact ? 'icon' : 'sm'}
                        className={compact ? 'h-7 w-7 shrink-0' : 'shrink-0'}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            setTyping(false);
                            setOpen(true);
                        }}
                        title="Open expression builder"
                    >
                        <Code2 className="h-3.5 w-3.5" />
                        {!compact && <span className="ml-1">Builder</span>}
                    </Button>
                </div>
            ) : (
                <PreviewButton
                    value={value}
                    placeholder={placeholder}
                    ctx={ctx}
                    compact={compact}
                    ariaLabel={ariaLabel}
                    onType={() => setTyping(true)}
                    onOpen={() => setOpen(true)}
                />
            )}
            {!validation.ok && validation.errors[0] && (
                <p className="mt-1 text-[10px] text-destructive">
                    {validation.errors[0].message}
                </p>
            )}
            {open && (
                <OelDialog
                    open={open}
                    onOpenChange={setOpen}
                    value={value}
                    onChange={onChange}
                    ctx={ctx}
                    placeholder={placeholder}
                    expectedType={expectedType}
                />
            )}
        </>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Inline preview button (read-only, click to open dialog)        */
/* ─────────────────────────────────────────────────────────────── */
function PreviewButton({
    value, placeholder, ctx, compact, ariaLabel, onType, onOpen
}) {
    const refs = useMemo(() => extractRefs(value), [value]);
    const validation = useMemo(() => validate(value, ctx), [value, ctx]);
    const tokens = useMemo(() => splitByRefs(value, refs), [value, refs]);

    return (
        <div className={cn('ds-oel-preview-btn', compact && 'compact')}>
            <button
                type="button"
                onClick={onType}
                className="ds-oel-preview-body"
                aria-label={ariaLabel || 'OEL expression — click to type'}
                title="Click to type directly"
            >
                {tokens.length === 0
                    ? <span className="ds-oel-preview-placeholder">{placeholder}</span>
                    : tokens.map((t, i) => t.kind === 'ref'
                        ? <RefChip key={i} ref_={t.ref} ctx={ctx} />
                        : <span key={i} className="ds-oel-preview-text">{t.text}</span>
                    )
                }
            </button>
            <span className="ds-oel-preview-actions">
                <span
                    className={cn('ds-oel-validity-mini', validation.ok ? 'ok' : 'bad')}
                    title={
                        validation.ok
                            ? 'Looks valid'
                            : validation.errors.map((e) => e.message).join(' · ')
                    }
                >
                    {validation.ok
                        ? <CheckCircle2 className="h-3 w-3" />
                        : <AlertCircle className="h-3 w-3" />}
                </span>
                <button
                    type="button"
                    onClick={onOpen}
                    className="ds-oel-preview-cta"
                    title="Open expression builder"
                    aria-label="Open expression builder"
                >
                    <Pencil className="h-3 w-3" />
                    {!compact && ' Edit'}
                </button>
            </span>
        </div>
    );
}

function splitByRefs(value, refs) {
    if (!value) return [];
    if (!refs.length) return [{
        kind: 'text',
        text: value
    }];
    const out = [];
    let cursor = 0;
    for (const r of refs) {
        if (r.start > cursor) out.push({
            kind: 'text',
            text: value.slice(cursor, r.start)
        });
        out.push({
            kind: 'ref',
            ref: r
        });
        cursor = r.end;
    }
    if (cursor < value.length) out.push({
        kind: 'text',
        text: value.slice(cursor)
    });
    return out;
}

function RefChip({
    ref_, ctx
}) {
    const isInput = ref_.root === 'input';
    const isSubflow = ref_.root === 'subflow';
    const isStep = (ctx?.previousSteps || []).some((s) => s.id === ref_.root);
    const isLoop = (ctx?.loopVars || []).some((l) => l.name === ref_.root);
    const tone = isInput ? 'violet' : isSubflow ? 'cyan' : isStep ? 'blue' : isLoop ? 'amber' : 'gray';
    return (
        <span className={cn('ds-oel-ref-chip', `ds-tone-${tone}`)}>
            {ref_.full}
        </span>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Dialog — large, inline panels, no popovers                     */
/* ─────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────── */
/*  Mention autocomplete helpers                                    */
/* ─────────────────────────────────────────────────────────────── */
function parseMention(text, caret) {
    const before = text.slice(0, caret);
    const pathMatch = /@([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_]*)?$/.exec(before);
    if (pathMatch) {
        return {
            kind: 'path',
            root: pathMatch[1],
            query: pathMatch[2] || '',
            startPos: pathMatch.index
        };
    }
    const rootMatch = /@([A-Za-z_][A-Za-z0-9_]*)?$/.exec(before);
    if (rootMatch) {
        return {
            kind: 'root',
            query: rootMatch[1] || '',
            startPos: rootMatch.index
        };
    }
    return null;
}

function getMentionItems(mention, ctx) {
    if (!mention) return [];
    const inputs = (ctx?.typedInputs?.length ? ctx.typedInputs : (ctx?.inputs || []).map(
        (i) => (typeof i === 'string' ? {
            name: i,
            type: 'any'
        } : i)
    ));
    const prevSteps = ctx?.previousSteps || [];

    if (mention.kind === 'root') {
        const q = mention.query.toLowerCase();
        const items = [];
        if (!q || 'input'.startsWith(q)) {
            items.push({
                label: 'input',
                full: '@input',
                tone: 'violet',
                sub: `${inputs.length} field${inputs.length !== 1 ? 's' : ''}`
            });
        }
        const subflowParams = ctx?.subflowParams || [];
        if (ctx?.subflowAvailable && (!q || 'subflow'.startsWith(q))) {
            items.push({
                label: 'subflow',
                full: '@subflow',
                tone: 'cyan',
                sub: subflowParams.length
                    ? `${subflowParams.length} With param${subflowParams.length !== 1 ? 's' : ''}`
                    : 'Subflow With parameters'
            });
        }
        for (const s of prevSteps) {
            if (!q || s.id.toLowerCase().startsWith(q)) {
                items.push({
                    label: s.id,
                    full: `@${s.id}`,
                    tone: 'blue',
                    sub: s.kind || 'step'
                });
            }
        }
        return items;
    }

    if (mention.kind === 'path') {
        const q = mention.query.toLowerCase();
        if (mention.root === 'input') {
            return inputs
                .filter((i) => !q || i.name.toLowerCase().startsWith(q))
                .map((i) => ({
                    label: i.name,
                    full: `@input.${i.name}`,
                    tone: 'violet',
                    sub: i.type || 'any'
                }));
        }
        if (mention.root === 'subflow') {
            const subflowParams = ctx?.subflowParams || [];
            return subflowParams
                .filter((p) => !q || p.name.toLowerCase().startsWith(q))
                .map((p) => ({
                    label: p.name,
                    full: `@subflow.${p.name}`,
                    tone: 'cyan',
                    sub: p.type || 'any'
                }));
        }
        const step = prevSteps.find((s) => s.id === mention.root);
        if (step) {
            const paths = [...outputPathsForStep(step.raw || step, ctx), ...STEP_META_PATHS];
            return paths
                .filter((p) => { const k = p.path.replace(/^\./, ''); return !q || k.toLowerCase().startsWith(q); })
                .map((p) => ({
                    label: p.path.replace(/^\./, ''),
                    full: `@${mention.root}${p.path}`,
                    tone: 'blue',
                    sub: `${p.type}${p.desc ? ' — ' + p.desc : ''}`
                }));
        }
    }
    return [];
}

function OelDialog({
    open, onOpenChange, value, onChange, ctx, placeholder, expectedType
}) {
    const [tab, setTab] = useState('refs'); // 'refs' | 'fns'
    const [draft, setDraft] = useState(value || '');
    const [mention, setMention] = useState(null);
    const [mentionIdx, setMentionIdx] = useState(0);
    const taRef = useRef(null);
    const caretRef = useRef({
        start: (value || '').length,
        end: (value || '').length
    });

    const refs = useMemo(() => extractRefs(draft), [draft]);
    const validation = useMemo(() => validate(draft, ctx), [draft, ctx]);

    function handleSave() {
        if (!validation.ok) return;
        onChange?.(draft);
        onOpenChange(false);
    }

    function captureCaret(el) {
        if (!el) return;
        caretRef.current = {
            start: el.selectionStart || 0,
            end: el.selectionEnd || 0
        };
    }

    const mentionItems = useMemo(() => getMentionItems(mention, ctx), [mention, ctx]);

    const handleMentionSelect = useCallback((item) => {
        const v = draft || '';
        const caret = caretRef.current.start;
        const next = v.slice(0, mention.startPos) + item.full + v.slice(caret);
        const newCaret = mention.startPos + item.full.length;
        setDraft(next);
        setMention(null);
        setMentionIdx(0);
        setTimeout(() => {
            const ta = taRef.current;
            if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret); caretRef.current = {
                start: newCaret,
                end: newCaret
            }; }
        }, 0);
    }, [draft, mention]);

    const insertSnippet = useCallback((snippet) => {
        const {
            start, end
        } = caretRef.current;
        const v = draft || '';
        if (snippet.startsWith('@')) {
            const before = v.slice(0, start);
            const partial = /@([A-Za-z_][A-Za-z0-9_]*)?(\.[A-Za-z_][A-Za-z0-9_]*)*\.?$/.exec(before);
            if (partial && partial.index !== undefined) {
                const {
                    next, caret
                } = replaceRange(v, partial.index, end, snippet);
                setDraft(next);
                setTimeout(() => {
                    const ta = taRef.current;
                    if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); caretRef.current = {
                        start: caret,
                        end: caret
                    }; }
                }, 0);
                return;
            }
        }
        let toInsert = snippet;
        const needsLeftSpace = toInsert.length && /[A-Za-z0-9_)\]'"]/.test(v.slice(start - 1, start)) && !toInsert.startsWith(' ');
        if (needsLeftSpace) toInsert = ' ' + toInsert;
        const {
            next, caret
        } = insertAt(v, start, toInsert);
        setDraft(next);
        setTimeout(() => {
            const ta = taRef.current;
            if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); caretRef.current = {
                start: caret,
                end: caret
            }; }
        }, 0);
    }, [draft, setDraft]);

    function removeRef(r) {
        const v = draft || '';
        const next = v.slice(0, r.start) + v.slice(r.end);
        setDraft(next.replace(/\s{2,}/g, ' '));
    }

    function clearAll() {
        setDraft('');
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl w-[95vw] ds-oel-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Code2 className="h-4 w-4 text-primary" />
                        OEL Expression Editor
                    </DialogTitle>
                    <DialogDescription>
                        Build the expression using the buttons below. Direct typing is allowed
                        inside this editor — outside of it, OEL fields are read-only.
                    </DialogDescription>
                </DialogHeader>

                <div className="ds-oel-dialog-body">
                    {/* ── Left column: composer + operators ─────────── */}
                    <div className="ds-oel-left-col">
                        {/* Composer — textarea + pills + errors */}
                        <div className="ds-oel-composer">
                            <div className="ds-oel-composer-header">
                                <span className="ds-oel-composer-label">Expression</span>
                                <div className="ds-oel-composer-meta">
                                    {expectedType && (
                                        <span className="ds-oel-expected-type">expects {expectedType}</span>
                                    )}
                                    <span
                                        className={cn(
                                            'ds-oel-validity',
                                            validation.ok ? 'ok' : 'bad'
                                        )}
                                        title={validation.ok ? 'Looks valid' : validation.errors.map((e) => e.message).join('\n')}
                                    >
                                        {validation.ok
                                            ? <><CheckCircle2 className="h-3 w-3" /> valid</>
                                            : <><AlertCircle className="h-3 w-3" /> {validation.errors.length} issue{validation.errors.length === 1 ? '' : 's'}</>}
                                    </span>
                                    <Button type="button" variant="ghost" size="sm" onClick={clearAll}
                                        className="h-7 px-2 text-[11px] text-muted-foreground hover:text-destructive">
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <div className="ds-oel-textarea-wrap">
                                <textarea
                                    ref={taRef}
                                    value={draft}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setDraft(v);
                                        captureCaret(e.target);
                                        const m = parseMention(v, e.target.selectionStart || 0);
                                        setMention(m);
                                        setMentionIdx(0);
                                    }}
                                    onClick={(e) => {
                                        captureCaret(e.target);
                                        setMention(parseMention(draft || '', e.target.selectionStart || 0));
                                        setMentionIdx(0);
                                    }}
                                    onKeyDown={(e) => {
                                        if (mention && mentionItems.length > 0) {
                                            if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionItems.length); return; }
                                            if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionItems.length) % mentionItems.length); return; }
                                            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleMentionSelect(mentionItems[mentionIdx]); return; }
                                            if (e.key === 'Escape') { setMention(null); return; }
                                        }
                                    }}
                                    onKeyUp={(e) => {
                                        captureCaret(e.target);
                                        if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                            setMention(parseMention(draft || '', e.target.selectionStart || 0));
                                            setMentionIdx(0);
                                        }
                                    }}
                                    onFocus={(e) => captureCaret(e.target)}
                                    onBlur={() => setTimeout(() => setMention(null), 150)}
                                    placeholder={placeholder}
                                    className="ds-oel-textarea"
                                    spellCheck={false}
                                    rows={7}
                                    autoFocus
                                />
                                {mention && mentionItems.length > 0 && (
                                    <MentionMenu
                                        items={mentionItems}
                                        activeIdx={mentionIdx}
                                        onSelect={handleMentionSelect}
                                    />
                                )}
                            </div>

                            {refs.length > 0 && (
                                <div className="ds-oel-pill-row">
                                    {refs.map((r, i) => {
                                        const isInput = r.root === 'input';
                                        const isStep = (ctx?.previousSteps || []).some((s) => s.id === r.root);
                                        const isLoop = (ctx?.loopVars || []).some((l) => l.name === r.root);
                                        const tone = isInput ? 'violet' : isStep ? 'blue' : isLoop ? 'amber' : 'gray';
                                        return (
                                            <span key={i} className={cn('ds-oel-pill', `ds-tone-${tone}`)}>
                                                <span className="ds-oel-pill-text">{r.full}</span>
                                                <button
                                                    type="button"
                                                    className="ds-oel-pill-x"
                                                    onClick={() => removeRef(r)}
                                                    aria-label={`Remove ${r.full}`}
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {!validation.ok && (
                                <div className="ds-oel-errors">
                                    {validation.errors.slice(0, 3).map((e, i) => (
                                        <div key={i} className="ds-oel-error-item">
                                            <AlertCircle className="h-3 w-3 inline mr-1" /> {e.message}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Operators + constants strip */}
                        <div className="ds-oel-section">
                            <div className="ds-oel-section-title">Operators &amp; Constants</div>
                            <div className="ds-oel-op-grid">
                                {OPERATOR_BUTTONS.map((op) => (
                                    <OpCard
                                        key={op.token}
                                        onClick={() => insertSnippet(op.spaced ? `${op.token} ` : op.token)}
                                        icon={op.icon}
                                        label={op.label}
                                        tone={op.tone}
                                        glyph={op.glyph || op.token}
                                    />
                                ))}
                                <span className="ds-oel-op-sep" />
                                {CONSTANT_BUTTONS.map((c) => (
                                    <OpCard
                                        key={c.token}
                                        onClick={() => insertSnippet(c.token)}
                                        icon={c.icon}
                                        label={c.label}
                                        tone={c.tone}
                                        glyph={c.token}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Right column: References / Functions panel ─ */}
                    <div className="ds-oel-right-col">
                        <div className="ds-oel-tabs">
                            <button
                                type="button"
                                className={cn('ds-oel-tab', tab === 'refs' && 'active')}
                                onClick={() => setTab('refs')}
                            >
                                <AtSign className="h-3.5 w-3.5" />
                                <span>References</span>
                                <span className="ds-oel-tab-count">
                                    {(ctx?.typedInputs?.length || 0) +
                                     (ctx?.previousSteps?.length || 0) +
                                     (ctx?.flows?.length || 0) +
                                     (ctx?.definitions?.length || 0)}
                                </span>
                            </button>
                            <button
                                type="button"
                                className={cn('ds-oel-tab', tab === 'fns' && 'active')}
                                onClick={() => setTab('fns')}
                            >
                                <FunctionSquare className="h-3.5 w-3.5" />
                                <span>Functions</span>
                                <span className="ds-oel-tab-count">{OEL_FUNCTIONS.length}</span>
                            </button>
                        </div>
                        <div className="ds-oel-tab-body">
                            {tab === 'refs' && <RefPanel ctx={ctx} onInsert={insertSnippet} />}
                            {tab === 'fns' && <FnPanel onInsert={insertSnippet} />}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t pt-3 mt-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={!validation.ok}
                        title={validation.ok ? undefined : validation.errors.map((e) => e.message).join(' · ')}>
                        <Save className="mr-1.5 h-3.5 w-3.5" /> Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Operator / constant card                                       */
/* ─────────────────────────────────────────────────────────────── */
function OpCard({
    onClick, icon: Icon, label, tone, glyph
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn('ds-oel-op-card', `tone-${tone}`)}
            title={label}
        >
            <span className="ds-oel-op-glyph">{glyph}</span>
            <span className="ds-oel-op-label">{label}</span>
        </button>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Inline mention autocomplete menu                               */
/* ─────────────────────────────────────────────────────────────── */
function MentionMenu({
    items, activeIdx, onSelect
}) {
    const listRef = useRef(null);
    useEffect(() => {
        const el = listRef.current?.children[activeIdx];
        el?.scrollIntoView({
            block: 'nearest'
        });
    }, [activeIdx]);

    return (
        <div className="ds-oel-mention-menu" role="listbox" ref={listRef}>
            {items.map((item, i) => (
                <button
                    key={item.full}
                    type="button"
                    role="option"
                    aria-selected={i === activeIdx}
                    className={cn('ds-oel-mention-item', i === activeIdx && 'active')}
                    onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
                >
                    <span className={cn('ds-oel-mention-dot', `ds-tone-${item.tone}`)} />
                    <span className={cn('ds-oel-mention-label', `ds-tone-${item.tone}`)}>{item.label}</span>
                    <span className="ds-oel-mention-sub">{item.sub}</span>
                </button>
            ))}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  References panel (inline, not a popover)                       */
/* ─────────────────────────────────────────────────────────────── */
function RefPanel({
    ctx, onInsert
}) {
    const [q, setQ] = useState('');
    const [expanded, setExpanded] = useState(null);

    const inputs = (ctx?.typedInputs && ctx.typedInputs.length)
        ? ctx.typedInputs
        : (ctx?.inputs || []).map((i) => (typeof i === 'string'
            ? {
                name: i,
                type: 'any'
            }
            : i));
    const prevSteps = ctx?.previousSteps || [];
    const flows = ctx?.flows || [];
    const defs = ctx?.definitions || [];
    const loops = ctx?.loopVars || [];
    const subflowParams = ctx?.subflowParams || [];

    const matches = (s) => !q.trim() || (s || '').toLowerCase().includes(q.trim().toLowerCase());

    const total = inputs.length + prevSteps.length + flows.length + defs.length + loops.length
        + (ctx?.subflowAvailable ? Math.max(subflowParams.length, 1) : 0);

    return (
        <div className="ds-oel-ref-panel">
            <div className="ds-oel-search">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search references…"
                    className="ds-oel-search-input"
                />
            </div>

            {total === 0 && (
                <div className="ds-oel-empty">
                    No references available yet — declare typed inputs or add steps to populate this menu.
                </div>
            )}

            {inputs.length > 0 && (
                <RefSection title="Opcode Inputs" icon={Variable} tone="violet">
                    {inputs.filter((i) => matches(i.name) || matches(i.type)).map((i) => (
                        <RefRow
                            key={`in-${i.name}`}
                            title={`@input.${i.name}`}
                            sub={`${i.type || 'any'}${i.required ? '' : ' · optional'}${i.description ? ' — ' + i.description : ''}`}
                            tone="violet"
                            icon={Variable}
                            onClick={() => onInsert(`@input.${i.name}`)}
                        />
                    ))}
                </RefSection>
            )}

            {ctx?.subflowAvailable && (
                <RefSection title="Subflow Parameters" icon={Workflow} tone="cyan">
                    {subflowParams.length === 0 && (
                        <RefRow
                            title="@subflow.<name>"
                            sub="Add With parameters on a Subflow step that calls this flow"
                            tone="cyan"
                            icon={Workflow}
                            onClick={() => onInsert('@subflow.')}
                        />
                    )}
                    {subflowParams.filter((p) => matches(p.name)).map((p) => (
                        <RefRow
                            key={`sf-${p.name}`}
                            title={`@subflow.${p.name}`}
                            sub={p.description || 'Passed from Subflow With (parameters)'}
                            tone="cyan"
                            icon={Workflow}
                            onClick={() => onInsert(`@subflow.${p.name}`)}
                        />
                    ))}
                </RefSection>
            )}

            {prevSteps.length > 0 && (
                <RefSection title="Previous Step Outputs" icon={GitFork} tone="blue">
                    {prevSteps.filter((s) => matches(s.id) || matches(s.kind)).map((s) => {
                        const isOpen = expanded === s.id;
                        const paths = [...outputPathsForStep(s.raw || s, ctx), ...STEP_META_PATHS];
                        return (
                            <div key={`s-${s.id}`} className="ds-oel-step-block">
                                <button
                                    type="button"
                                    onClick={() => setExpanded(isOpen ? null : s.id)}
                                    className="ds-oel-step-head"
                                >
                                    <span className="ds-oel-pick-icon ds-tone-blue">
                                        <GitFork size={12} />
                                    </span>
                                    <span className="ds-oel-step-id">@{s.id}</span>
                                    <span className="ds-oel-step-kind">{s.kind || 'step'} · {paths.length} paths</span>
                                    <span className="ds-oel-step-chev">{isOpen ? '▾' : '▸'}</span>
                                </button>
                                {isOpen && (
                                    <div className="ds-oel-step-paths">
                                        {paths.map((p) => (
                                            <button
                                                key={p.path}
                                                type="button"
                                                onClick={() => onInsert(`@${s.id}${p.path}`)}
                                                className="ds-oel-path-row"
                                            >
                                                <span className="ds-oel-path-name">
                                                    @{s.id}<span className="ds-oel-path-tail">{p.path}</span>
                                                </span>
                                                <span className="ds-oel-path-meta">
                                                    <span className="ds-oel-type-badge">{p.type}</span>
                                                    {p.desc}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </RefSection>
            )}

            {flows.length > 0 && !ctx?.inFlowScope && (
                <RefSection title="Flows" icon={Workflow} tone="cyan">
                    {flows.filter((f) => matches(f.id)).map((f) => (
                        <RefRow
                            key={`f-${f.id}`}
                            title={`@${f.id}`}
                            sub="flow"
                            tone="cyan"
                            icon={Workflow}
                            onClick={() => onInsert(`@${f.id}`)}
                        />
                    ))}
                </RefSection>
            )}

            {defs.length > 0 && (
                <RefSection title="Definitions" icon={GitFork} tone="indigo">
                    {defs.filter((d) => matches(d.id) || matches(d.kind)).map((d) => (
                        <RefRow
                            key={`d-${d.id}`}
                            title={`@${d.id}.output`}
                            sub={`${d.kind || 'definition'} · ${d.output_type || 'any'}`}
                            tone="indigo"
                            icon={GitFork}
                            onClick={() => onInsert(`@${d.id}.output`)}
                        />
                    ))}
                </RefSection>
            )}

            {loops.length > 0 && (
                <RefSection title="Loop Variables" icon={Repeat} tone="amber">
                    {loops.filter((l) => matches(l.name)).map((l) => (
                        <RefRow
                            key={`l-${l.name}`}
                            title={`@${l.name}`}
                            sub={l.source ? `from ${l.source}` : 'loop var'}
                            tone="amber"
                            icon={Repeat}
                            onClick={() => onInsert(`@${l.name}`)}
                        />
                    ))}
                </RefSection>
            )}
        </div>
    );
}

function RefSection({
    title, icon: Icon, tone, children
}) {
    return (
        <div className="ds-oel-ref-section">
            <div className={cn('ds-oel-ref-section-title', `tone-${tone}`)}>
                <Icon className="h-3 w-3" />
                <span>{title}</span>
            </div>
            <div className="ds-oel-ref-section-body">
                {children}
            </div>
        </div>
    );
}

function RefRow({
    title, sub, tone, icon: Icon, onClick
}) {
    return (
        <button type="button" onClick={onClick} className="ds-oel-ref-row">
            <span className={cn('ds-oel-pick-icon', `ds-tone-${tone}`)}>
                <Icon size={12} />
            </span>
            <span className="ds-oel-ref-row-body">
                <span className="ds-oel-ref-row-title">{title}</span>
                <span className="ds-oel-ref-row-sub">{sub}</span>
            </span>
        </button>
    );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Functions panel                                                */
/* ─────────────────────────────────────────────────────────────── */
function FnPanel({
    onInsert
}) {
    const [q, setQ] = useState('');
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return OEL_FUNCTIONS;
        return OEL_FUNCTIONS.filter((f) =>
            f.name.toLowerCase().includes(s) ||
            f.desc.toLowerCase().includes(s) ||
            f.category.toLowerCase().includes(s)
        );
    }, [q]);
    const grouped = useMemo(() => {
        const out = {};
        for (const f of filtered) (out[f.category] ||= []).push(f);
        return out;
    }, [filtered]);

    return (
        <div className="ds-oel-fn-panel">
            <div className="ds-oel-search">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search functions…"
                    className="ds-oel-search-input"
                />
            </div>

            {Object.keys(grouped).length === 0 && (
                <div className="ds-oel-empty">No matches.</div>
            )}

            {Object.entries(grouped).map(([cat, fns]) => (
                <div key={cat} className="ds-oel-fn-section">
                    <div className="ds-oel-fn-cat">{cat}</div>
                    <div className="ds-oel-fn-grid">
                        {fns.map((f) => (
                            <button
                                key={f.name}
                                type="button"
                                onClick={() => onInsert(`${f.name}(`)}
                                className="ds-oel-fn-card"
                            >
                                <span className="ds-oel-fn-sig"><FunctionSquare className="h-3 w-3" /> {f.sig}</span>
                                <span className="ds-oel-fn-desc">{f.desc}</span>
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
